import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Button, Center, Drawer, Flex, Pagination, Stack, Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { IconGraph } from "@tabler/icons-react";

import { fetchAndDispatchPregnancyRecords } from "@/pregnancy";
import { useAppDispatch } from "@/store";
import { selectAllPatientPregnancyRecords } from "@/store/slices/pregnancy";

import {
  CHART_EXPORT_SECTIONS,
  ChartExportSectionKeys,
  InitialBillingSectionsOptions,
  InitialClientProfileSectionsOptions,
  InitialIntakeFormSectionsOptions,
  InitialProblemsAllergiesMedicationsSectionsOptions,
} from "../constants/exportOptions";
import {
  ENCOUNTER_TABLE_COLUMNS,
  FILES_TABLE_COLUMNS,
  IMAGING_TABLE_COLUMNS,
  LAB_TABLE_COLUMNS,
} from "../constants/tables";
import { useCheckboxList } from "../hooks/useCheckboxList";
import { useClientExportData } from "../hooks/useClientExportData";
import { ClientExportSelectSectionDrawerProps } from "../interfaces/select-section-drawer";
import { CheckboxItem, EncounterOption, FileOption, LabOption } from "../types/export";
import {
  mapEncounterDataToOptions,
  mapFileDataToOptions,
  mapLabOrderDataToOptions,
} from "../utils";
import { ExportFormSchema, ExportFormSchemaType } from "../zod-schemas/export-form";

import { EmptyStateTable } from "./EmptyStateTable";
import { EncounterTable } from "./EncounterTable";
import { FilesTable } from "./FilesTable";
import { FormCheckboxSectionHeader } from "./FormCheckboxSectionHeader";
import { FormCheckboxSectionList } from "./FormCheckboxSectionList";
import { LabsTable } from "./LabsTable";
import { TableLoadingIndicator } from "./TableLoadingIndicator";

function ClientExportSelectSectionDrawer({
  clientId,
  practiceId: practiceIdProp,
  opened,
  onClose,
}: ClientExportSelectSectionDrawerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoading,
    practiceId,
    encountersData,
    labOrdersData,
    filesData,
    encountersTotal,
    labOrdersTotal,
    filesTotal,
    changePage,
    currentPage,
    fetchAllEncounterIds,
    fetchAllLabOrderIds,
    fetchAllFileIds,
  } = useClientExportData(clientId, opened, practiceIdProp);
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const isProjectAdmin = useMemo(() => medplum.isProjectAdmin(), [medplum]);

  const pregnancyRecordsSelector = useMemo(
    () => selectAllPatientPregnancyRecords(clientId),
    [clientId],
  );
  const allPregnancyRecords = useSelector(pregnancyRecordsSelector);

  // Ensure pregnancy records are in Redux regardless of how the drawer was opened.
  // When opened from the client list, the records may not have been fetched yet.
  useEffect(() => {
    if (!opened) return;
    fetchAndDispatchPregnancyRecords(medplum, dispatch, clientId, { status: "current,retired" });
  }, [medplum, dispatch, clientId, opened]);

  const encounterOptions = useMemo<EncounterOption[]>(
    () => mapEncounterDataToOptions(encountersData, allPregnancyRecords),
    [encountersData, allPregnancyRecords],
  );

  const labOptions = useMemo<LabOption[]>(
    () => mapLabOrderDataToOptions(labOrdersData),
    [labOrdersData],
  );

  const fileOptions = useMemo<FileOption[]>(() => mapFileDataToOptions(filesData), [filesData]);

  // Keyed by section so the render, reset, and form-sync paths can all walk CHART_EXPORT_SECTIONS.
  const sectionCheckboxLists = {
    [ChartExportSectionKeys.CLIENT_PROFILE]: useCheckboxList<CheckboxItem>(
      InitialClientProfileSectionsOptions,
      opened,
    ),
    [ChartExportSectionKeys.BILLING]: useCheckboxList<CheckboxItem>(
      InitialBillingSectionsOptions,
      opened,
    ),
    [ChartExportSectionKeys.PROBLEMS_ALLERGIES_MEDICATIONS]: useCheckboxList<CheckboxItem>(
      InitialProblemsAllergiesMedicationsSectionsOptions,
      opened,
    ),
    [ChartExportSectionKeys.INTAKE_FORM]: useCheckboxList<CheckboxItem>(
      InitialIntakeFormSectionsOptions,
      opened,
    ),
  };
  const encounters = useCheckboxList(encounterOptions, opened, {
    onSelectAll: fetchAllEncounterIds,
  });
  const labs = useCheckboxList(labOptions, opened, { onSelectAll: fetchAllLabOrderIds });
  const files = useCheckboxList(fileOptions, opened, { onSelectAll: fetchAllFileIds });

  // Named locals so the form-sync effect below has statically checkable dependencies.
  const clientProfileItems = sectionCheckboxLists[ChartExportSectionKeys.CLIENT_PROFILE].items;
  const billingItems = sectionCheckboxLists[ChartExportSectionKeys.BILLING].items;
  const problemsAllergiesMedicationsItems =
    sectionCheckboxLists[ChartExportSectionKeys.PROBLEMS_ALLERGIES_MEDICATIONS].items;
  const intakeFormItems = sectionCheckboxLists[ChartExportSectionKeys.INTAKE_FORM].items;

  const exportForm = useForm<ExportFormSchemaType>({
    initialValues: {
      clientProfileSections: [] as string[],
      problemsAllergiesMedicationsSections: [] as string[],
      billingSections: [] as string[],
      intakeFormSections: [] as string[],
      encounterIds: [] as string[],
      encounterFlowsheetIds: [] as string[],
      labOrderIds: [] as string[],
      fileIds: [] as string[],
    },
    validate: zodResolver(ExportFormSchema),
  });

  useEffect(() => {
    if (!opened) return;
    CHART_EXPORT_SECTIONS.forEach((section) => {
      exportForm.setFieldValue(
        section.formField,
        sectionCheckboxLists[section.key].getSelectedIds(),
      );
    });
    exportForm.setFieldValue("encounterIds", encounters.getSelectedIds());
    exportForm.setFieldValue(
      "encounterFlowsheetIds",
      encounters.items
        .filter((item) => item.checked && item.flowsheetChecked)
        .map((item) => item.key),
    );
    exportForm.setFieldValue("labOrderIds", labs.getSelectedIds());
    exportForm.setFieldValue("fileIds", files.getSelectedIds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientProfileItems,
    billingItems,
    problemsAllergiesMedicationsItems,
    intakeFormItems,
    encounters.items,
    labs.items,
    files.items,
    opened,
  ]);

  const handleClose = () => {
    exportForm.reset();
    CHART_EXPORT_SECTIONS.forEach((section) => {
      sectionCheckboxLists[section.key].handler.setState(section.options);
    });
    encounters.handler.setState([]);
    labs.handler.setState([]);
    files.handler.setState([]);
    onClose();
  };

  const handleSubmit = (values: ExportFormSchemaType) => {
    navigate(`/practices/${practiceId}/clients/${clientId}/export`, {
      state: {
        clientProfileSections: values.clientProfileSections,
        billingSections: values.billingSections,
        encounterIds: values.encounterIds,
        intakeFormSections: values.intakeFormSections,
        encounterFlowsheetIds: values.encounterFlowsheetIds,
        labOrderIds: values.labOrderIds,
        fileIds: values.fileIds,
        previousLocation: location.pathname,
        problemsAllergiesMedicationsSections: values.problemsAllergiesMedicationsSections,
      },
    });
  };

  return (
    <Drawer.Root
      size="60%"
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <form onSubmit={exportForm.onSubmit(handleSubmit)}>
          <Drawer.Header>
            <Flex gap="sm" align="center">
              <Drawer.CloseButton size={24} ms="unset" />
              <Drawer.Title fz="h4">Export Client Chart</Drawer.Title>
            </Flex>
            <Button type="submit" variant="filled" loading={isLoading}>
              Export Chart
            </Button>
          </Drawer.Header>

          <Drawer.Body>
            <Stack>
              {CHART_EXPORT_SECTIONS.map((section) => {
                const checkboxList = sectionCheckboxLists[section.key];

                return (
                  <Stack gap="sm" key={section.key}>
                    <FormCheckboxSectionHeader
                      title={section.title}
                      disabled={!section.showSelectAll}
                      onToggleAll={checkboxList.toggleAll}
                      showAllCheckbox={section.showSelectAll}
                      allChecked={section.showSelectAll && checkboxList.allChecked}
                      someChecked={section.showSelectAll && checkboxList.someChecked}
                    />
                    <FormCheckboxSectionList
                      items={checkboxList.items}
                      onItemChange={checkboxList.onItemChange}
                    />
                  </Stack>
                );
              })}

              <Stack gap="sm">
                <FormCheckboxSectionHeader
                  title="Encounters"
                  allChecked={encounters.allChecked}
                  onToggleAll={encounters.toggleAll}
                  someChecked={encounters.someChecked}
                  disabled={isLoading || encounters.items.length === 0}
                />
                {isLoading ? (
                  <TableLoadingIndicator columns={ENCOUNTER_TABLE_COLUMNS} />
                ) : (
                  <>
                    <EncounterTable
                      items={encounters.items}
                      onItemChange={encounters.onItemChange}
                      onFlowsheetChange={(index, checked) =>
                        encounters.handler.setItemProp(index, "flowsheetChecked", checked)
                      }
                    />
                    <Center>
                      <Pagination
                        hideWithOnePage
                        disabled={isLoading}
                        total={encountersTotal}
                        value={currentPage.encounters}
                        onChange={(value) => changePage(value, "encounters")}
                      />
                    </Center>
                  </>
                )}
              </Stack>

              <Stack gap="sm">
                <FormCheckboxSectionHeader
                  title="Labs"
                  allChecked={labs.allChecked}
                  onToggleAll={labs.toggleAll}
                  someChecked={labs.someChecked}
                  disabled={isLoading || labs.items.length === 0}
                />
                {isLoading ? (
                  <TableLoadingIndicator columns={LAB_TABLE_COLUMNS} />
                ) : (
                  <>
                    <LabsTable items={labs.items} onItemChange={labs.onItemChange} />
                    <Center>
                      <Pagination
                        hideWithOnePage
                        disabled={isLoading}
                        total={labOrdersTotal}
                        value={currentPage.labOrders}
                        onChange={(value) => changePage(value, "labOrders")}
                      />
                    </Center>
                  </>
                )}
              </Stack>

              {isProjectAdmin && (
                <Stack gap="sm">
                  <FormCheckboxSectionHeader
                    title="Imaging"
                    disabled={true}
                    allChecked={false}
                    someChecked={false}
                    onToggleAll={() => {}}
                  />
                  <EmptyStateTable
                    columns={IMAGING_TABLE_COLUMNS}
                    message="There are currently no imaging for this client."
                    icon={<IconGraph size={18} color="var(--mantine-color-gray-6)" />}
                  />
                </Stack>
              )}

              <Stack gap="sm">
                <FormCheckboxSectionHeader
                  title="Files"
                  allChecked={files.allChecked}
                  onToggleAll={files.toggleAll}
                  someChecked={files.someChecked}
                  disabled={isLoading || files.items.length === 0}
                />
                <Text size="xs" c="dimmed">
                  Only PDF and image files are shown
                </Text>
                {isLoading ? (
                  <TableLoadingIndicator columns={FILES_TABLE_COLUMNS} />
                ) : (
                  <>
                    <FilesTable items={files.items} onItemChange={files.onItemChange} />
                    <Center>
                      <Pagination
                        hideWithOnePage
                        total={filesTotal}
                        disabled={isLoading}
                        value={currentPage.files}
                        onChange={(value) => changePage(value, "files")}
                      />
                    </Center>
                  </>
                )}
              </Stack>
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}

export default ClientExportSelectSectionDrawer;
